import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  typesService,
  HardwareType,
  ModelReference,
  HardwareTypeUpdate,
  ModelReferenceCreate,
  HardwareFieldDefinition,
  HardwareFieldDefinitionCreate,
  HardwareFieldDefinitionUpdate,
  FieldGrouping,
  ValueType
} from '../../services/typesService';
import { getTypeBadgeColor } from '../../utils/fieldTypes';
import AuditHistoryTab from '../audit/AuditHistoryTab';
import HardwareSchemaJsonEditor from './HardwareSchemaJsonEditor';
import { useI18n } from '../../contexts/I18nContext';

interface HardwareTypeDetailModalProps {
  hardwareType: HardwareType | null;
  modelRefs: ModelReference[];
  isOpen: boolean;
  canEdit?: boolean;
  onClose: () => void;
  onTypeUpdated: () => void;
}

interface FormErrors {
  code?: string;
  name?: string;
  description?: string;
}

// Value types accepted by the backend's FieldDefinitionCreate model.
// Must stay in sync with provisioningv2 app/models/field_definition.py
// (ValueType Literal). 'dropdown', 'json', 'email', 'ip_address' are
// frontend-only and would 422 on the /v2/hardware-types/{id}/field-defs
// endpoint, so they're NOT offered here.
const VALUE_TYPES: { value: ValueType; label: string }[] = [
  { value: 'string', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'json', label: 'JSON' },
  { value: 'array', label: 'Array' },
  { value: 'password', label: 'Password' },
  { value: 'url', label: 'URL' },
  { value: 'hostname', label: 'Hostname' },
  { value: 'port', label: 'Port' },
  { value: 'select', label: 'Dropdown (select)' },
];

// Backend-enforced regex for field_name. Mirrors the Pydantic pattern
// ^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z][a-zA-Z0-9_]*)*$ (app/models/
// field_definition.py) so we can surface a helpful client-side error
// instead of a generic 422. Names are freeform — camelCase and
// snake_case are both valid, with optional dot-separated segments.
const FIELD_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z][a-zA-Z0-9_]*)*$/;

/**
 * Format an Axios error from the backend into a human-readable message.
 * Handles FastAPI's structured validation error body (`detail` as a list
 * of {loc, msg, type} dicts) by joining the individual field messages.
 */
const formatBackendError = (err: any): string => {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const loc = Array.isArray(d?.loc) ? d.loc.filter((p: unknown) => p !== 'body').join('.') : '';
        const msg = d?.msg || 'validation error';
        return loc ? `${loc}: ${msg}` : msg;
      })
      .join('; ');
  }
  if (typeof detail === 'string') return detail;
  return err?.message || 'Unknown error';
};

export const HardwareTypeDetailModal: React.FC<HardwareTypeDetailModalProps> = ({
  hardwareType,
  modelRefs,
  isOpen,
  canEdit = false,
  onClose,
  onTypeUpdated
}) => {
  const navigate = useNavigate();
  const { formatDateTime, t } = useI18n();
  const [activeTab, setActiveTab] = useState<'overview' | 'modelrefs' | 'fielddefs' | 'devices' | 'history'>('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [localModelRefs, setLocalModelRefs] = useState<ModelReference[]>([]);
  const [showJsonEditor, setShowJsonEditor] = useState(false);

  // Field definitions state
  const [fieldDefs, setFieldDefs] = useState<HardwareFieldDefinition[]>([]);
  const [schemaDefaults, setSchemaDefaults] = useState<Record<string, unknown> | null>(null);
  const [groupings, setGroupings] = useState<FieldGrouping[]>([]);
  const [editingFieldId, setEditingFieldId] = useState<number | null>(null);
  const [editingFieldData, setEditingFieldData] = useState<HardwareFieldDefinitionUpdate>({});
  const [showAddField, setShowAddField] = useState(false);
  const [newField, setNewField] = useState<HardwareFieldDefinitionCreate>({
    field_name: '',
    display_name: '',
    value_type: 'string',
    is_required: false,
    display_order: 0
  });

  // Free-form grouping input. User types a name (or picks from the datalist);
  // on submit we look up an existing grouping by name/code and create one
  // if none matches. The grouping_id on `newField` is resolved at submit time.
  const [groupingInput, setGroupingInput] = useState('');

  // Editable fields
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [rebootPayload, setRebootPayload] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [refAnep, setRefAnep] = useState<string[]>([]);
  const [newRefAnep, setNewRefAnep] = useState('');

  // New model ref
  const [selectedRefCode, setSelectedRefCode] = useState<string>('');
  const [availableModelRefs, setAvailableModelRefs] = useState<ModelReference[]>([]);

  useEffect(() => {
    if (hardwareType) {
      setCode(hardwareType.code);
      setName(hardwareType.name);
      setDescription(hardwareType.description || '');
      setRebootPayload(hardwareType.reboot_payload || '');
      setIsActive(hardwareType.is_active);
      setRefAnep(hardwareType.ref_anep || []);
      setNewRefAnep('');
      setLocalModelRefs(modelRefs);
      setError(null);
      setSuccessMessage(null);
      setErrors({});
      setIsEditing(false);
      setActiveTab('overview');
      setFieldDefs([]);
      setShowAddField(false);
      resetNewField();
      setSelectedRefCode('');
    }
    // Re-seeding form state is driven by the selected hardware type and its
    // external modelRefs prop. resetNewField is a stable local function that
    // doesn't need to trigger a re-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hardwareType, modelRefs]);

  // Load field definitions and groupings when tab is selected
  useEffect(() => {
    if (isOpen && hardwareType && activeTab === 'fielddefs') {
      loadFieldDefinitions();
      loadGroupings();
    }
    // Loader functions are stable within the component — their references
    // change on every render but we only want this effect tied to the tab
    // switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hardwareType, activeTab]);

  // Load all available model references when modelrefs tab is selected
  useEffect(() => {
    if (isOpen && hardwareType && activeTab === 'modelrefs') {
      loadAvailableModelRefs();
    }
  }, [isOpen, hardwareType, activeTab]);

  const loadAvailableModelRefs = async () => {
    try {
      // Fetch only active hardware types and their active model refs —
      // soft-deleted ones must never appear as "available to assign".
      const allHardwareTypes = await typesService.getHardwareTypes(false);
      const allRefs: ModelReference[] = [];

      for (const hwType of allHardwareTypes) {
        const refs = await typesService.getModelReferences(hwType.id, false);
        refs.forEach(ref => allRefs.push(ref));
      }

      setAvailableModelRefs(allRefs);
    } catch (err) {
      console.error('Failed to load available model references:', err);
    }
  };

  const loadFieldDefinitions = async () => {
    if (!hardwareType) return;
    setIsLoading(true);
    try {
      const response = await typesService.getHardwareFieldDefinitions(hardwareType.id);
      setFieldDefs(response.items);
    } catch (err) {
      console.error('Failed to load field definitions:', err);
      setError('Failed to load field definitions');
    } finally {
      setIsLoading(false);
    }
    // Load assembled defaults in parallel (best-effort)
    typesService.getHardwareTypeSchema(hardwareType.code)
      .then((schema) => setSchemaDefaults(schema?.default ?? null))
      .catch(() => setSchemaDefaults(null));
  };

  const loadGroupings = async () => {
    try {
      const grps = await typesService.getFieldGroupings();
      setGroupings(grps);
    } catch (err) {
      console.error('Failed to load groupings:', err);
    }
  };

  const resetNewField = () => {
    setNewField({
      field_name: '',
      display_name: '',
      value_type: 'string',
      is_required: false,
      expose_on_wire: false,
      wire_root: false,
      display_order: fieldDefs.length
    });
    setGroupingInput('');
  };

  if (!isOpen || !hardwareType) return null;

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!code.trim()) {
      newErrors.code = 'Device code is required';
    } else if (!/^[A-Z0-9_-]{1,20}$/.test(code.trim())) {
      newErrors.code = 'Code must be uppercase letters, numbers, underscores or dashes';
    }

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    } else if (name.trim().length > 100) {
      newErrors.name = 'Name must not exceed 100 characters';
    }

    if (description && description.length > 500) {
      newErrors.description = 'Description must not exceed 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    setError(null);
    setSuccessMessage(null);

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const updateData: HardwareTypeUpdate = {
        code: code.trim(),
        name: name.trim(),
        description: description.trim() || undefined,
        reboot_payload: rebootPayload.trim() || undefined,
        is_active: isActive,
        ref_anep: refAnep
      };

      await typesService.updateHardwareType(hardwareType.id, updateData);

      setSuccessMessage('Hardware type updated successfully');
      setIsEditing(false);
      onTypeUpdated();
    } catch (err: any) {
      console.error('[HARDWARE TYPE DETAIL] Error saving:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to update hardware type';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setCode(hardwareType.code);
    setName(hardwareType.name);
    setDescription(hardwareType.description || '');
    setRebootPayload(hardwareType.reboot_payload || '');
    setIsActive(hardwareType.is_active);
    setRefAnep(hardwareType.ref_anep || []);
    setNewRefAnep('');
    setIsEditing(false);
    setError(null);
    setErrors({});
  };

  // Get available model references (active refs not already assigned to this hardware type)
  const getAvailableRefs = (): ModelReference[] => {
    if (!hardwareType) return [];
    const existingCodes = new Set(localModelRefs.map(r => r.code.toUpperCase()));

    // Filter out refs that are already assigned to this hardware type (by code)
    return availableModelRefs.filter(ref =>
      !existingCodes.has(ref.code.toUpperCase())
    );
  };

  const handleAddModelRef = async () => {
    if (!selectedRefCode) {
      setError('Please select a model reference to add');
      return;
    }

    // Find the selected ref to get its details
    const selectedRef = availableModelRefs.find(r => r.code === selectedRefCode);

    setIsLoading(true);
    setError(null);

    try {
      const data: ModelReferenceCreate = {
        code: selectedRefCode.toUpperCase(),
        name: selectedRef?.name || `${hardwareType.name} ${selectedRefCode}`,
        description: selectedRef?.description
      };
      await typesService.createModelReference(hardwareType.id, data);

      // Refresh model refs
      const refs = await typesService.getModelReferences(hardwareType.id, true);
      setLocalModelRefs(refs);
      setSelectedRefCode('');
      setSuccessMessage('Model reference added successfully');
    } catch (err: any) {
      setError('Failed to add model reference: ' + (err.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveModelRef = async (refId: number) => {
    if (!window.confirm('Are you sure you want to remove this model reference?')) return;

    setIsLoading(true);
    setError(null);

    try {
      await typesService.deleteModelReference(refId);
      const refs = await typesService.getModelReferences(hardwareType.id, true);
      setLocalModelRefs(refs);
      setSuccessMessage('Model reference removed');
    } catch (err: any) {
      setError('Failed to remove model reference: ' + (err.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddFieldDef = async () => {
    if (!hardwareType) return;
    if (!newField.field_name.trim() || !newField.display_name.trim()) {
      setError('Field name and display name are required');
      return;
    }

    // Normalise and validate the field name against the backend's
    // Pydantic pattern so we can show a clear error instead of a 422.
    // Case is preserved — camelCase and snake_case are both valid.
    const normalizedFieldName = newField.field_name
      .trim()
      .replace(/\s+/g, '_')
      .replace(/-+/g, '_');
    if (!FIELD_NAME_PATTERN.test(normalizedFieldName)) {
      setError(
        `Field name "${normalizedFieldName}" is invalid. Must start with a ` +
          'letter and contain only letters, digits, and underscores — ' +
          'camelCase or snake_case (e.g. mqtt_host, modeAvion, api_key_2).',
      );
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Resolve the free-form grouping input to an existing id, creating
      // a new grouping on the fly if the typed text matches nothing.
      let groupingId: number | undefined;
      try {
        groupingId = await resolveGroupingInput(groupingInput);
      } catch (err: any) {
        setError(
          'Failed to create grouping: ' +
            (err?.response?.data?.detail || err?.message || 'Unknown error'),
        );
        setIsLoading(false);
        return;
      }

      await typesService.createHardwareFieldDefinition(hardwareType.id, {
        ...newField,
        grouping_id: groupingId,
        field_name: normalizedFieldName,
        display_order: fieldDefs.length
      });
      await loadFieldDefinitions();
      setShowAddField(false);
      resetNewField();
      setSuccessMessage('Field definition added');
    } catch (err: any) {
      setError('Failed to add field: ' + formatBackendError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteFieldDef = async (field: HardwareFieldDefinition) => {
    if (!hardwareType) return;

    // Check impact first
    const impact = await typesService.checkFieldImpact(hardwareType.id, field.id);
    if (impact.warning_message) {
      const confirmMsg = `${impact.warning_message}\n\nAre you sure you want to delete this field?`;
      if (!window.confirm(confirmMsg)) return;
    } else {
      if (!window.confirm(`Delete field "${field.display_name}"?`)) return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await typesService.deleteHardwareFieldDefinition(field.id, field.is_system);
      await loadFieldDefinitions();
      setSuccessMessage('Field definition deleted');
    } catch (err: any) {
      setError('Failed to delete field: ' + (err.response?.data?.detail || err.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  const startEditField = (field: HardwareFieldDefinition) => {
    setEditingFieldId(field.id);
    setEditingFieldData({
      display_name: field.display_name,
      value_type: field.value_type as ValueType,
      default_value: field.default_value ?? '',
      is_required: field.is_required,
      grouping_id: field.grouping_id ?? undefined,
      help_text: field.help_text ?? '',
      display_order: field.display_order ?? 0,
      expose_on_wire: !!field.expose_on_wire,
      wire_root: !!field.wire_root,
    });
  };

  const cancelEditField = () => {
    setEditingFieldId(null);
    setEditingFieldData({});
  };

  const saveEditField = async () => {
    if (editingFieldId === null) return;
    setIsLoading(true);
    setError(null);
    try {
      await typesService.updateHardwareFieldDefinition(editingFieldId, editingFieldData);
      setEditingFieldId(null);
      setEditingFieldData({});
      await loadFieldDefinitions();
      setSuccessMessage('Field updated');
    } catch (err: any) {
      setError('Failed to update field: ' + (err.response?.data?.detail || err.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Resolve a free-form grouping input string to an existing grouping id,
   * creating a new grouping if no match is found.
   *
   * Matching rules (case-insensitive, trimmed):
   *   1. Exact match on grouping.code
   *   2. Exact match on grouping.name
   *
   * On no match: POST /v2/field-groupings with a derived code (slug of the
   * typed name) and the typed name as the display name.
   *
   * Returns `undefined` when input is empty (field has no grouping).
   * Throws on backend failure — caller is expected to catch and display.
   */
  const resolveGroupingInput = async (raw: string): Promise<number | undefined> => {
    const text = raw.trim();
    if (!text) return undefined;

    const lower = text.toLowerCase();
    const match =
      groupings.find((g) => g.code.toLowerCase() === lower) ||
      groupings.find((g) => g.name.toLowerCase() === lower);
    if (match) return match.id;

    // Derive a slug for the code: lowercase, spaces -> underscores,
    // strip anything outside [a-z0-9_]. Fall back to a timestamp suffix
    // if the slug collides with an existing code.
    const baseSlug =
      text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '') || `group_${Date.now()}`;
    let code = baseSlug;
    if (groupings.some((g) => g.code === code)) {
      code = `${baseSlug}_${Date.now().toString(36)}`;
    }

    const created = await typesService.createFieldGrouping({
      code,
      name: text,
      display_order: groupings.length * 10,
    });
    // Refresh in-memory list so subsequent calls in the same session see it
    await loadGroupings();
    return created.id;
  };

  // Group field definitions by grouping
  const groupedFieldDefs = fieldDefs.reduce((acc, field) => {
    const groupName = field.grouping_name || 'Ungrouped';
    if (!acc[groupName]) {
      acc[groupName] = [];
    }
    acc[groupName].push(field);
    return acc;
  }, {} as Record<string, HardwareFieldDefinition[]>);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="device-type-detail-modal hardware-type-detail-modal" data-testid="modal-hardware-type-detail" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <span className="type-icon-large">{hardwareType.icon || '🔧'}</span>
            <div>
              <h2>{hardwareType.name}</h2>
              <span className="type-code-badge">{hardwareType.code}</span>
            </div>
          </div>
          <button className="close-button" data-testid="btn-modal-close" onClick={onClose} aria-label={t('common.close')}>
            &times;
          </button>
        </div>

        <div className="modal-tabs">
          <button
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`tab-button ${activeTab === 'modelrefs' ? 'active' : ''}`}
            onClick={() => setActiveTab('modelrefs')}
          >
            Model Refs ({localModelRefs.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'fielddefs' ? 'active' : ''}`}
            data-testid="tab-field-defs"
            onClick={() => setActiveTab('fielddefs')}
          >
            Field Defs ({fieldDefs.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'devices' ? 'active' : ''}`}
            onClick={() => setActiveTab('devices')}
          >
            Devices ({hardwareType.device_count || 0})
          </button>
          <button
            className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            History
          </button>
        </div>

        <div className="modal-content">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="success-message">
              {successMessage}
            </div>
          )}

          {activeTab === 'overview' && (
            <div className="tab-content">
              <div className="info-grid">
                <div className="info-item">
                  <label>Status</label>
                  {isEditing ? (
                    <select
                      className="edit-select status-select"
                      value={isActive ? 'active' : 'inactive'}
                      onChange={(e) => setIsActive(e.target.value === 'active')}
                    >
                      <option value="active">ACTIVE</option>
                      <option value="inactive">INACTIVE</option>
                    </select>
                  ) : (
                    <span className={`status-badge ${hardwareType.is_active ? 'active' : 'inactive'}`}>
                      {hardwareType.is_active ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  )}
                </div>
                <div className="info-item">
                  <label>{t('deviceTypes.deviceTypeCode')} {isEditing && '*'}</label>
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        data-testid="field-hardware-type-code-edit"
                        value={code}
                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                        className={errors.code ? 'error' : ''}
                        placeholder="e.g., PGA, IOTL"
                        maxLength={20}
                      />
                      {errors.code && <span className="field-error" data-testid="error-hardware-type-code">{errors.code}</span>}
                    </>
                  ) : (
                    <span className="monospace">{hardwareType.code}</span>
                  )}
                </div>
                <div className="info-item">
                  <label>{t('deviceTypes.modelRefs')}</label>
                  <span className="device-count-value">{localModelRefs.length}</span>
                </div>
                <div className="info-item full-width">
                  <label>{t('deviceTypes.name')} {isEditing && '*'}</label>
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        data-testid="field-hardware-type-name-edit"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className={errors.name ? 'error' : ''}
                        placeholder={t('deviceTypes.hardwareTypeName')}
                      />
                      {errors.name && <span className="field-error" data-testid="error-hardware-type-name">{errors.name}</span>}
                    </>
                  ) : (
                    <span>{hardwareType.name}</span>
                  )}
                </div>
                <div className="info-item full-width">
                  <label>{t('common.description')}</label>
                  {isEditing ? (
                    <>
                      <textarea
                        data-testid="field-hardware-type-description-edit"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={3}
                        placeholder={t('deviceTypes.hardwareTypeDescription')}
                        className={errors.description ? 'error' : ''}
                      />
                      {errors.description && <span className="field-error">{errors.description}</span>}
                    </>
                  ) : (
                    <span>{hardwareType.description || '-'}</span>
                  )}
                </div>
                <div className="info-item full-width">
                  <label>{t('deviceTypes.rebootPayload')}</label>
                  {isEditing ? (
                    <>
                      <div className="reboot-input-wrap">
                        <span className="reboot-input-icon" aria-hidden="true">⏻</span>
                        <input
                          type="text"
                          className="reboot-input"
                          data-testid="field-hardware-type-reboot-payload-edit"
                          value={rebootPayload}
                          onChange={(e) => setRebootPayload(e.target.value)}
                          placeholder="97#"
                          maxLength={16}
                          autoCapitalize="off"
                          autoCorrect="off"
                          spellCheck={false}
                        />
                      </div>
                      <span className="reboot-hint">{t('deviceTypes.rebootPayloadHelp')}</span>
                    </>
                  ) : hardwareType.reboot_payload ? (
                    <span className="reboot-chip" data-testid="value-hardware-type-reboot-payload">
                      <span className="reboot-chip-icon" aria-hidden="true">⏻</span>
                      <code>{hardwareType.reboot_payload}</code>
                    </span>
                  ) : (
                    <span className="reboot-chip reboot-chip--default" data-testid="value-hardware-type-reboot-payload">
                      {t('deviceTypes.rebootPayloadDefault')}
                    </span>
                  )}
                </div>
                <div className="info-item full-width">
                  <label>{t('deviceTypes.anepReferences')}</label>
                  {isEditing ? (
                    <div className="ref-anep-editor">
                      <div className="ref-anep-list">
                        {refAnep.map((ref, index) => (
                          <div key={index} className="ref-anep-item">
                            <span>{ref}</span>
                            <button
                              type="button"
                              className="btn btn-danger btn-small"
                              onClick={() => setRefAnep(refAnep.filter((_, i) => i !== index))}
                            >
                              {t('users.remove')}
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="ref-anep-add">
                        <input
                          type="text"
                          value={newRefAnep}
                          onChange={(e) => setNewRefAnep(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const val = newRefAnep.trim();
                              if (val && !refAnep.includes(val)) {
                                setRefAnep([...refAnep, val]);
                                setNewRefAnep('');
                              }
                            }
                          }}
                          placeholder="e.g., A-EA-022 P4GU VoLTE"
                        />
                        <button
                          type="button"
                          className="btn btn-primary btn-small"
                          onClick={() => {
                            const val = newRefAnep.trim();
                            if (val && !refAnep.includes(val)) {
                              setRefAnep([...refAnep, val]);
                              setNewRefAnep('');
                            }
                          }}
                        >
                          {t('common.create')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="ref-anep-badges">
                      {(hardwareType.ref_anep || []).length > 0 ? (
                        (hardwareType.ref_anep || []).map((ref, index) => (
                          <span key={index} className="model-ref-badge">{ref}</span>
                        ))
                      ) : (
                        <span>-</span>
                      )}
                    </div>
                  )}
                </div>
                <div className="info-item">
                  <label>{t('clients.created')}</label>
                  <span>{formatDateTime(hardwareType.created_at)}</span>
                </div>
                <div className="info-item">
                  <label>{t('deviceTypes.lastUpdated')}</label>
                  <span>{formatDateTime(hardwareType.updated_at)}</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'modelrefs' && (
            <div className="tab-content">
              <div className="model-refs-section">
                <h3>{t('deviceTypes.modelReferences')}</h3>

                {localModelRefs.length > 0 ? (
                  <div className="model-refs-table">
                    <table>
                      <thead>
                        <tr>
                          <th>{t('deviceTypes.code')}</th>
                          <th>{t('deviceTypes.name')}</th>
                          <th>{t('clients.status')}</th>
                          {canEdit && <th>{t('deviceTypes.actions')}</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {localModelRefs.map((ref) => (
                          <tr key={ref.id}>
                            <td><span className="type-code">{ref.code}</span></td>
                            <td>{ref.name || '-'}</td>
                            <td>
                              <span className={`status-badge small ${ref.is_active ? 'active' : 'inactive'}`}>
                                {ref.is_active ? t('common.active') : t('common.inactive')}
                              </span>
                            </td>
                            {canEdit && (
                              <td>
                                <button
                                  className="btn btn-danger btn-small"
                                  onClick={() => handleRemoveModelRef(ref.id)}
                                  disabled={isLoading}
                                >
                                  Remove
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-refs">
                    <span className="empty-icon">📋</span>
                    <p>{t('deviceTypes.noModelReferencesFound')}</p>
                  </div>
                )}

                {canEdit && (
                  <div className="add-model-ref-section">
                    <h4>{t('deviceTypes.addModelReference')}</h4>
                    <div className="add-ref-dropdown-form">
                      <select
                        className="model-ref-dropdown"
                        value={selectedRefCode}
                        onChange={(e) => setSelectedRefCode(e.target.value)}
                        disabled={isLoading || getAvailableRefs().length === 0}
                      >
                        <option value="">{t('deviceTypes.addModelReference')}...</option>
                        {getAvailableRefs().map((ref) => (
                          <option key={ref.id} value={ref.code}>
                            {ref.code} {ref.name ? `- ${ref.name}` : ''}
                          </option>
                        ))}
                      </select>
                      <button
                        className="btn btn-primary"
                        onClick={handleAddModelRef}
                        disabled={isLoading || !selectedRefCode}
                      >
                        {isLoading ? t('users.adding') : t('common.create')}
                      </button>
                    </div>
                    {getAvailableRefs().length === 0 && availableModelRefs.length > 0 && (
                      <p className="no-suggestions-text">{t('deviceTypes.allModelRefsAdded')}</p>
                    )}
                    {availableModelRefs.length === 0 && (
                      <p className="no-suggestions-text">{t('deviceTypes.noModelRefsYet')}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'fielddefs' && (
            <div className="tab-content">
              <div className="field-defs-section">
                <div className="section-header">
                  <div>
                    <h3>{t('deviceTypes.fieldDefinitions')}</h3>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {canEdit && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          setShowJsonEditor((prev) => !prev);
                          // Close the single-field form if the JSON editor
                          // takes over — avoid having two editors open.
                          if (!showJsonEditor) setShowAddField(false);
                        }}
                      >
                        {showJsonEditor ? `${t('common.close')} JSON` : `${t('common.edit')} JSON`}
                      </button>
                    )}
                    {canEdit && !showAddField && !showJsonEditor && (
                      <button
                        className="btn btn-primary"
                        data-testid="btn-add-field-def"
                        onClick={() => setShowAddField(true)}
                        disabled={isLoading}
                      >
                        + Add Field
                      </button>
                    )}
                  </div>
                </div>

                {showJsonEditor && (
                  <HardwareSchemaJsonEditor
                    hardwareTypeId={hardwareType.id}
                    hardwareTypeCode={hardwareType.code}
                    canEdit={canEdit}
                    groupings={groupings}
                    resolveGroupingInput={resolveGroupingInput}
                    onSchemaChanged={loadFieldDefinitions}
                    onClose={() => setShowJsonEditor(false)}
                  />
                )}

                {showAddField && (
                  <div className="add-field-form" data-testid="form-add-field-def">
                    <div className="add-field-form__header">
                      <span className="add-field-form__icon" aria-hidden="true">+</span>
                      <h4>{t('deviceTypes.addNewField')}</h4>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="required">{t('devices.fieldName')} <span className="required-asterisk">*</span></label>
                        <input
                          type="text"
                          data-testid="field-fielddef-name"
                          placeholder="e.g., mqtt_port"
                          value={newField.field_name}
                          onChange={(e) => setNewField({ ...newField, field_name: e.target.value })}
                          disabled={isLoading}
                        />
                        <small>camelCase or snake_case, no spaces</small>
                      </div>
                      <div className="form-group">
                        <label className="required">{t('hardwareSchema.fieldDisplayName')} <span className="required-asterisk">*</span></label>
                        <input
                          type="text"
                          data-testid="field-fielddef-display-name"
                          placeholder="e.g., MQTT Port"
                          value={newField.display_name}
                          onChange={(e) => setNewField({ ...newField, display_name: e.target.value })}
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>{t('deviceTypes.valueType')}</label>
                        <select
                          value={newField.value_type}
                          data-testid="field-fielddef-value-type"
                          onChange={(e) => setNewField({ ...newField, value_type: e.target.value as ValueType })}
                          disabled={isLoading}
                        >
                          {VALUE_TYPES.map(vt => (
                            <option key={vt.value} value={vt.value}>{vt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Grouping</label>
                        <input
                          type="text"
                          list="field-grouping-options"
                          placeholder={t('deviceTypes.groupingPlaceholder')}
                          value={groupingInput}
                          onChange={(e) => setGroupingInput(e.target.value)}
                          disabled={isLoading}
                        />
                        <datalist id="field-grouping-options">
                          {groupings.map((g) => (
                            <option key={g.id} value={g.name}>
                              {g.code}
                            </option>
                          ))}
                        </datalist>
                        <small>
                          Matches an existing grouping by name or code
                          (case-insensitive); otherwise a new grouping is
                          created on save.
                        </small>
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>{t('deviceTypes.defaultValue')}</label>
                        <input
                          type="text"
                          data-testid="field-fielddef-default-value"
                          placeholder={t('deviceTypes.optionalDefault')}
                          value={newField.default_value || ''}
                          onChange={(e) => setNewField({ ...newField, default_value: e.target.value })}
                          disabled={isLoading}
                        />
                      </div>
                      <div className="form-group">
                        <label>{t('hardwareSchema.placeholder')}</label>
                        <input
                          type="text"
                          placeholder={t('deviceTypes.optionalPlaceholder')}
                          value={newField.placeholder || ''}
                          onChange={(e) => setNewField({ ...newField, placeholder: e.target.value })}
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                    <div className="add-field-form__options">
                      <div className="form-group checkbox-group">
                        <label>
                          <input
                            type="checkbox"
                            checked={newField.is_required || false}
                            onChange={(e) => setNewField({ ...newField, is_required: e.target.checked })}
                            disabled={isLoading}
                          />
                          {t('deviceTypes.requiredField')}
                        </label>
                      </div>
                      <div className="form-group checkbox-group">
                        <label title={t('deviceTypes.exposeFlagTitleEdit')}>
                          <input
                            type="checkbox"
                            data-testid="field-fielddef-expose-on-wire"
                            checked={newField.expose_on_wire || false}
                            onChange={(e) => setNewField({ ...newField, expose_on_wire: e.target.checked })}
                            disabled={isLoading}
                          />
                          {t('deviceTypes.exposeDefaultToFirmware')}
                        </label>
                      </div>
                      <div className="form-group checkbox-group">
                        <label title={t('deviceTypes.wireRootTitle')}>
                          <input
                            type="checkbox"
                            data-testid="field-fielddef-wire-root"
                            checked={newField.wire_root || false}
                            onChange={(e) => setNewField({ ...newField, wire_root: e.target.checked })}
                            disabled={isLoading}
                          />
                          {t('deviceTypes.wireRootLabel')}
                        </label>
                      </div>
                    </div>
                    <div className="form-actions">
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          setShowAddField(false);
                          resetNewField();
                        }}
                        disabled={isLoading}
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        className="btn btn-primary"
                        data-testid="btn-submit-field-def"
                        onClick={handleAddFieldDef}
                        disabled={isLoading || !newField.field_name.trim() || !newField.display_name.trim()}
                      >
                        {isLoading ? t('users.adding') : t('deviceTypes.addField')}
                      </button>
                    </div>
                  </div>
                )}

                {isLoading && fieldDefs.length === 0 ? (
                  <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>{t('deviceTypes.loadingFieldDefs')}</p>
                  </div>
                ) : fieldDefs.length > 0 ? (
                  <div className="field-defs-list">
                    {Object.entries(groupedFieldDefs).map(([groupName, fields]) => (
                      <div key={groupName} className="field-group">
                        <h4 className="group-header">{groupName}</h4>
                        <table className="field-defs-table">
                          <thead>
                            <tr>
                              <th>{t('devices.fieldName')}</th>
                              <th>{t('hardwareSchema.fieldDisplayName')}</th>
                              <th>{t('devices.typeLabel')}</th>
                              <th>{t('deviceTypes.required')}</th>
                              <th title={t('deviceTypes.exposeFlagColumnHeader')}>{t('deviceTypes.expose')}</th>
                              <th title={t('deviceTypes.wireRootTitle')}>{t('deviceTypes.wireRootColumn')}</th>
                              <th>{t('hardwareSchema.fieldDefault')}</th>
                              {canEdit && <th>{t('deviceTypes.actions')}</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {fields.map((field) => (
                              editingFieldId === field.id ? (
                                <tr key={field.id} className="editing-row">
                                  <td><code>{field.field_name}</code></td>
                                  <td>
                                    <input
                                      type="text"
                                      className="cell-input"
                                      value={editingFieldData.display_name ?? ''}
                                      onChange={(e) => setEditingFieldData(d => ({ ...d, display_name: e.target.value }))}
                                    />
                                  </td>
                                  <td>
                                    <select
                                      className="cell-select"
                                      value={editingFieldData.value_type ?? 'string'}
                                      onChange={(e) => setEditingFieldData(d => ({ ...d, value_type: e.target.value as ValueType }))}
                                      style={{ color: getTypeBadgeColor(editingFieldData.value_type ?? 'string') }}
                                    >
                                      {(['string','number','boolean','password','url','hostname','port','select','json','array'] as const).map(t => (
                                        <option key={t} value={t}>{t}</option>
                                      ))}
                                    </select>
                                  </td>
                                  <td>
                                    <label className="cell-checkbox">
                                      <input
                                        type="checkbox"
                                        checked={editingFieldData.is_required ?? false}
                                        onChange={(e) => setEditingFieldData(d => ({ ...d, is_required: e.target.checked }))}
                                      />
                                    </label>
                                  </td>
                                  <td>
                                    <label className="cell-checkbox" title={t('deviceTypes.exposeFlagInlineEdit')}>
                                      <input
                                        type="checkbox"
                                        checked={editingFieldData.expose_on_wire ?? false}
                                        onChange={(e) => setEditingFieldData(d => ({ ...d, expose_on_wire: e.target.checked }))}
                                      />
                                    </label>
                                  </td>
                                  <td>
                                    <label className="cell-checkbox" title={t('deviceTypes.wireRootTitle')}>
                                      <input
                                        type="checkbox"
                                        checked={editingFieldData.wire_root ?? false}
                                        onChange={(e) => setEditingFieldData(d => ({ ...d, wire_root: e.target.checked }))}
                                      />
                                    </label>
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      className="cell-input mono"
                                      value={editingFieldData.default_value ?? ''}
                                      onChange={(e) => setEditingFieldData(d => ({ ...d, default_value: e.target.value }))}
                                      placeholder="—"
                                    />
                                  </td>
                                  <td>
                                    <div className="field-actions-group">
                                      <button
                                        className="btn-icon btn-icon-save"
                                        onClick={saveEditField}
                                        disabled={isLoading}
                                        title={t('common.save')}
                                      >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                          <polyline points="20 6 9 17 4 12"/>
                                        </svg>
                                      </button>
                                      <button
                                        className="btn-icon"
                                        onClick={cancelEditField}
                                        title={t('common.cancel')}
                                      >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <line x1="18" y1="6" x2="6" y2="18"/>
                                          <line x1="6" y1="6" x2="18" y2="18"/>
                                        </svg>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ) : (
                                <tr key={field.id} className={field.is_system ? 'system-field' : ''}>
                                  <td><code>{field.field_name}</code></td>
                                  <td>{field.display_name}</td>
                                  <td>
                                    <span className="type-badge" style={{ backgroundColor: getTypeBadgeColor(field.value_type) }}>
                                      {field.value_type}
                                    </span>
                                  </td>
                                  <td>
                                    {field.is_required ? (
                                      <span className="required-badge">Yes</span>
                                    ) : (
                                      <span className="optional-badge">No</span>
                                    )}
                                  </td>
                                  <td>
                                    {field.expose_on_wire ? (
                                      <span className="required-badge" title={t('deviceTypes.exposeBadgeOn')}>{t('common.yes')}</span>
                                    ) : (
                                      <span className="optional-badge" title={t('deviceTypes.exposeBadgeOff')}>{t('common.no')}</span>
                                    )}
                                  </td>
                                  <td>
                                    {field.wire_root ? (
                                      <span className="required-badge" title={t('deviceTypes.wireRootBadgeOn')}>{t('common.yes')}</span>
                                    ) : (
                                      <span className="optional-badge" title={t('deviceTypes.wireRootBadgeOff')}>{t('common.no')}</span>
                                    )}
                                  </td>
                                  <td>{field.default_value || '-'}</td>
                                  {canEdit && (
                                    <td>
                                      <div className="field-actions-group">
                                        <button
                                          className="btn-icon"
                                          onClick={() => startEditField(field)}
                                          disabled={isLoading || editingFieldId !== null}
                                          title={t('deviceTypes.editField')}
                                        >
                                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                          </svg>
                                        </button>
                                        <button
                                          className="btn-icon btn-icon-danger"
                                          onClick={() => handleDeleteFieldDef(field)}
                                          disabled={isLoading || editingFieldId !== null}
                                          title={field.is_system ? 'System field - will require force delete' : 'Delete field'}
                                        >
                                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="3 6 5 6 21 6"/>
                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                            <line x1="10" y1="11" x2="10" y2="17"/>
                                            <line x1="14" y1="11" x2="14" y2="17"/>
                                          </svg>
                                        </button>
                                      </div>
                                    </td>
                                  )}
                                </tr>
                              )
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-fields">
                    <span className="empty-icon">📝</span>
                    <p>No field definitions configured for this hardware type.</p>
                    {canEdit && (
                      <p className="hint">Click "Add Field" to define configuration fields.</p>
                    )}
                  </div>
                )}

                {schemaDefaults && Object.keys(schemaDefaults).length > 0 && (
                  <details className="schema-defaults-inline">
                    <summary>
                      Assembled Defaults
                      <span className="schema-defaults-count">{Object.keys(schemaDefaults).length} fields</span>
                    </summary>
                    <div className="schema-defaults-grid">
                      {Object.entries(schemaDefaults).map(([key, value]) => (
                        <div key={key} className="schema-default-row">
                          <code className="schema-default-key">{key}</code>
                          <span className="schema-default-value">
                            {value === null || value === undefined
                              ? '—'
                              : typeof value === 'object'
                              ? JSON.stringify(value)
                              : String(value) || '—'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            </div>
          )}

          {activeTab === 'devices' && (
            <div className="tab-content">
              <div className="devices-placeholder">
                <div className="placeholder-icon">📱</div>
                <h3>{hardwareType.device_count || 0} Devices</h3>
                <p>
                  View all devices of hardware type <strong>{hardwareType.name}</strong> ({hardwareType.code})
                  in the Devices page with the type filter applied.
                </p>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    onClose();
                    navigate(`/devices?type=${hardwareType.code}`);
                  }}
                >
                  View Devices
                </button>
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <AuditHistoryTab
              resourceType="hardware_type"
              resourceId={String(hardwareType.id)}
            />
          )}
        </div>

        <div className="modal-actions">
          {isEditing ? (
            <>
              <button
                className="btn btn-secondary"
                data-testid="btn-hardware-type-cancel"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                data-testid="btn-hardware-type-save"
                onClick={handleSave}
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary" data-testid="btn-hardware-type-close" onClick={onClose}>
                Close
              </button>
              {canEdit && (
                <button
                  className="btn btn-primary"
                  data-testid="btn-hardware-type-edit"
                  onClick={() => {
                    setIsEditing(true);
                    setSuccessMessage(null);
                  }}
                >
                  Edit
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
